<template>
    <hox-modal @close="close">
        <hox-loading-layer v-if="isLoadingUpdateSection" />
        <DeleteCampaignSectionModal
            v-if="deleteCampaignSectionModalIsVisible"
            :section-id="sectionId"
            :section-name="sectionName"
            @sectionDeleted="deleted"
            @close="toggleDeleteCampaignSectionModalIsVisible"
        />
        <template #header>Edit section</template>
        <CampaignSectionForm
            :initial-form-data="formData"
            @hasValidationErrorsUpdate="setHasValidationErrors"
            @formDataUpdate="setFormData"
            @submit="updateSection"
        />
        <div v-if="hasErrorUpdatingSection" class="edit-campaign-section-modal__unexpected-error-wrapper">
            <Alert banner type="error">
                <template #desc>
                    <div v-if="mutationErrors">
                        <p v-for="error in mutationErrors" :key="error" class="error-wrapper">Error: {{ error }}</p>
                    </div>

                    <p v-else>
                        There was an unexpected error and the section could not be updated. Please try again in a few
                        moments.
                    </p>
                </template>
            </Alert>
        </div>
        <template #footer>
            <div class="edit-campaign-section-modal__buttons-container">
                <Button
                    ghost
                    type="error"
                    data-testid="duplicate-section-modal__button--delete"
                    @click="toggleDeleteCampaignSectionModalIsVisible"
                >
                    Delete
                </Button>
                <Button
                    type="primary"
                    data-testid="duplicate-section-modal__button--save"
                    :disabled="hasValidationErrors || hasErrorUpdatingSection"
                    @click="updateSection"
                >
                    Save
                </Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import updateCampaignSectionMutation from "@/apollo/mutations/UpdateCampaignSection.gql";

import CampaignSectionForm from "@/components/CampaignSectionForm.vue";
import DeleteCampaignSectionModal from "@/components/DeleteCampaignSectionModal.vue";
import type { ApolloError } from "@apollo/client/core";

export default {
    components: {
        CampaignSectionForm,
        DeleteCampaignSectionModal
    },

    props: {
        sectionId: {
            type: String,
            required: true
        },
        sectionName: {
            type: String,
            required: true
        }
    },

    emits: ["close", "deleted", "sectionUpdated"],

    data() {
        return {
            deleteCampaignSectionModalIsVisible: false,
            formData: {
                name: this.sectionName
            },
            hasErrorUpdatingSection: false,
            hasValidationErrors: false,
            isLoadingUpdateSection: false,
            mutationErrors: [] as string[]
        };
    },

    watch: {
        "formData.name": function () {
            this.mutationErrors = [];
            this.hasErrorUpdatingSection = false;
        }
    },

    methods: {
        async updateSection() {
            if (this.hasValidationErrors) {
                return;
            }

            this.hasErrorUpdatingSection = false;
            this.isLoadingUpdateSection = true;

            try {
                await this.$apollo.mutate({
                    mutation: updateCampaignSectionMutation,
                    variables: {
                        sectionId: this.sectionId,
                        name: this.formData.name
                    }
                });

                this.$emit("sectionUpdated");
                this.close();
            } catch (error) {
                this.hasErrorUpdatingSection = true;

                const graphQLErrors = (error as ApolloError)?.graphQLErrors || [];
                this.mutationErrors = graphQLErrors.map(({ message }) => message);
            } finally {
                this.isLoadingUpdateSection = false;
            }
        },

        close() {
            this.$emit("close");
        },

        deleted() {
            this.$emit("deleted");
            this.close();
        },

        setFormData(formData: { name: string }) {
            this.formData = formData;
        },

        setHasValidationErrors(hasValidationErrors: boolean) {
            this.hasValidationErrors = hasValidationErrors;
        },

        toggleDeleteCampaignSectionModalIsVisible() {
            this.deleteCampaignSectionModalIsVisible = !this.deleteCampaignSectionModalIsVisible;
        }
    }
};
</script>

<style scoped lang="scss">
@use "@style/variables" as *;

.edit-campaign-section-modal__buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.edit-campaign-section-modal__unexpected-error-wrapper {
    padding: $spacing 0 0 0;
}
</style>
